import React, { useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Image from 'next/image';
import { BaseFeaturedPackagingCard, SizeOption } from '@/shared/types/featured-packaging';
import { App, Checkbox } from 'antd';
import { formatCurrency } from '@/shared/utils/utils';
import { calculateDisplayPrice, getQuantityOptions, getSizeAsString } from '@/shared/utils/featured-packaging-util';
import { PACKAGING_PROMO_CODE } from '@/shared/constants/featured-packaging-constants';
import s3ImageTransform from '@/shared/utils/s3ImageTransform';
import { LogisticsFeeRate } from '@/shared/types/store';
import { first, keys, round, size } from 'lodash-es';
import './index.less';

const MAX_SIZES_SHOWN = 3;
const MAX_QUANTITIES_SHOWN = 4;

interface IProps {
  cardInfo: BaseFeaturedPackagingCard;
  selectTitle: string;
  buttonTitle: string;
  bundleLogisticsFee: boolean;
  logisticsFeeRateLadder: LogisticsFeeRate[];
  onNext(size: string, quantity: number): void;
}

const FeaturedPackagingCardElement = ({
  cardInfo,
  selectTitle,
  buttonTitle,
  bundleLogisticsFee,
  logisticsFeeRateLadder,
  onNext,
}: IProps) => {
  const [selectedSize, setSelectedSize] = useState<string>();
  const [selectedQuantity, setSelectedQuantity] = useState<number>();

  const { message } = App.useApp();

  const { options } = cardInfo;
  const types = keys(options);
  const firstType: string = first(types);
  const sizeOptions: SizeOption[] = options[firstType].sizes;
  const firstSize: SizeOption = first(sizeOptions);
  const quantityOptions = getQuantityOptions(options, firstType, selectedSize);

  useEffect(() => {
    if (firstSize) {
      setSelectedSize(getSizeAsString(firstSize));
    }
    const firstQty: string = first(keys(quantityOptions));
    if (firstQty) {
      setSelectedQuantity(parseInt(firstQty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardInfo]);

  const onButtonClick = () => {
    if (!selectedSize) {
      message.error('Please select a size.');
      return;
    }

    if (!selectedQuantity) {
      message.error('Please select a quantity.');
      return;
    }

    onNext(selectedSize, selectedQuantity);
  };

  const promoCode = (
    <CopyToClipboard
      className="promo-pill"
      text={PACKAGING_PROMO_CODE}
      onCopy={() => message.success(`The promo code ${PACKAGING_PROMO_CODE} was copied to your clipboard.`)}
    >
      <span>10% off order! Click to copy.</span>
    </CopyToClipboard>
  );

  const mobileImageContent = (
    <div className="image mobile">
      <div className="top">
        <Image
          src={s3ImageTransform(cardInfo.image, 'compact')}
          alt={cardInfo.startingPointTitle}
          width={80}
          height={80}
        />
        <h2 className="title" style={{ color: cardInfo.textColor }}>
          {cardInfo.startingPointTitle}
        </h2>
      </div>
      {promoCode}
    </div>
  );

  const desktopImageContent = (
    <div className="image desktop">
      <div className="top">
        <h2 className="title" style={{ color: cardInfo.textColor }}>
          {cardInfo.startingPointTitle}
        </h2>
        {promoCode}
      </div>
      <div className="bottom">
        <Image src={s3ImageTransform(cardInfo.image, 'large')} alt={cardInfo.startingPointTitle} fill={true} />
      </div>
    </div>
  );

  const computeSavings = (unitCost: number) => {
    const baseUnitCost = quantityOptions[first(keys(quantityOptions))];
    return round((1 - unitCost / baseUnitCost) * 100, 0);
  };

  const textContent = (
    <div className="content">
      <div className="form-container">
        <div className="section">
          <h3 className="title">{selectTitle}</h3>
          <div className="options">
            {(sizeOptions || []).slice(0, MAX_SIZES_SHOWN).map((sizeOption: SizeOption, i: number) => {
              const sizeAsString = getSizeAsString(sizeOption);
              return (
                <Checkbox
                  checked={selectedSize === sizeAsString}
                  key={i}
                  onChange={() => {
                    setSelectedSize(sizeAsString);
                  }}
                >
                  <span className="size-option">
                    {sizeAsString} {sizeOption.description && <span className="desc">({sizeOption.description})</span>}
                  </span>
                </Checkbox>
              );
            })}
            {size(sizeOptions) > MAX_SIZES_SHOWN && (
              <span
                className="show-all"
                onClick={() => {
                  onNext(selectedSize, selectedQuantity);
                }}
              >
                See more sizing options
              </span>
            )}
          </div>
        </div>
        <div className="section">
          <span className="title">Select a quantity</span>
          <div className="options">
            {keys(quantityOptions)
              .slice(0, MAX_QUANTITIES_SHOWN)
              .map((qty: string, i: number) => {
                const unitCost = quantityOptions[qty];
                const displayUnitCost = bundleLogisticsFee
                  ? calculateDisplayPrice(unitCost, logisticsFeeRateLadder)
                  : unitCost;

                const savings = i === 0 ? 0 : computeSavings(unitCost);

                return (
                  <Checkbox
                    checked={selectedQuantity === parseInt(qty)}
                    key={i}
                    onChange={() => setSelectedQuantity(parseInt(qty))}
                  >
                    <div className="quantity-option">
                      <span>{qty}</span>
                      <span>
                        {formatCurrency(displayUnitCost, !Number.isInteger(round(displayUnitCost, 2)))}
                        <span className="ea">/ea</span>
                      </span>
                      <span className="savings">{savings > 0 ? `Save ${savings}%` : ''}</span>
                    </div>
                  </Checkbox>
                );
              })}
            {size(quantityOptions) > MAX_QUANTITIES_SHOWN && (
              <span
                className="show-all"
                onClick={() => {
                  onNext(selectedSize, selectedQuantity);
                }}
              >
                See more quantity options
              </span>
            )}
          </div>
        </div>
      </div>
      <span
        className="action-button black"
        onClick={() => {
          onButtonClick();
        }}
      >
        {buttonTitle}
      </span>
    </div>
  );

  return (
    <div className="featured-packaging-card">
      <div className="inner" style={{ backgroundColor: cardInfo.cardColor }}>
        {mobileImageContent}
        {desktopImageContent}
        {textContent}
      </div>
    </div>
  );
};

export default FeaturedPackagingCardElement;
